import cn from "classnames";
import { type ComponentPropsWithoutRef, type PropsWithChildren } from "react";
import BackgroundVideo from "@/components/common/BackgroundVideo";
import { getBackgroundColorClassName } from "@/utils/style/getBackgroundColorClassName";
import { getBackgroundGradientClassName } from "@/utils/style/getBackgroundGradientClassName";
import { getModuleContainerStyle } from "@/utils/style/getModuleContainerStyle";
import type { BaseSiteBuilderModule } from "@/types/site-builder";
import BackgroundImage from "./BackgroundImage";
import BackgroundOverlay from "./BackgroundOverlay";
import BackgroundPattern from "./BackgroundPattern";
import s from "./ModuleUI.module.scss";

export interface ModuleUIProps extends ComponentPropsWithoutRef<"section"> {
  module: BaseSiteBuilderModule;
}

export default function ModuleUI(props: PropsWithChildren<ModuleUIProps>) {
  const {
    module: {
      style: {
        background: {
          color: { colorVariant, gradientVariant },
          pattern: patternSettings,
          image: imageSettings,
          video: videoSettings,
          overlay: overlaySettings,
        },
      },
      settings: { container: containerSettings },
    },
    style: customStyle,
    children,
    ...restProps
  } = props;

  const className = cn(s.wrapper, restProps.className);
  const backgroundColorClassName = getBackgroundColorClassName(colorVariant);
  const backgroundGradientClassName =
    getBackgroundGradientClassName(gradientVariant);

  const containerStyle = getModuleContainerStyle(containerSettings);
  const style = { ...containerStyle, ...customStyle };

  return (
    <section {...restProps} className={className} style={style}>
      {backgroundColorClassName && (
        <div className={cn(s.overlay, backgroundColorClassName)} />
      )}
      {backgroundGradientClassName && (
        <div className={cn(s.overlay, backgroundGradientClassName)} />
      )}
      {patternSettings.enabled && (
        <BackgroundPattern settings={patternSettings} />
      )}
      {imageSettings.enabled && <BackgroundImage settings={imageSettings} />}
      {videoSettings.enabled && videoSettings.videoId && (
        <BackgroundVideo
          source={videoSettings.source}
          videoId={videoSettings.videoId}
        />
      )}
      {overlaySettings.enabled && (
        <BackgroundOverlay settings={overlaySettings} />
      )}
      <div className={s.content}>{children}</div>
    </section>
  );
}
